import { useEval, DIMENSION_COLORS } from '@/context/EvalContext';
import { useEffect, useState } from 'react';

const CX = 185;
const CY = 175; // 稍稍上移由于大半圆下边距较空
const R_GAUGE = 125;

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = (angleDeg * Math.PI) / 180.0;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(x: number, y: number, r: number, startAngle: number, endAngle: number) {
  const start = polarToCartesian(x, y, r, startAngle);
  const end = polarToCartesian(x, y, r, endAngle);
  // 对于SVG A命令，当扫描角度 > 180度，largeArcFlag需取1
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
}

function getScoreColor(score: number): string {
  if (score >= 90) return '#06b6d4'; // 极优：青蓝 (Cyan)
  if (score >= 80) return '#10b981'; // 良好：翠绿 (Emerald)
  if (score >= 70) return '#f59e0b'; // 中等：金黄 (Amber)
  if (score >= 60) return '#f97316'; // 及格：橙色 (Orange)
  return '#ef4444'; // 不及格：赤红 (Red)
}

function ScoreCard({ dim, align }: { dim: any; align: 'left' | 'right' }) {
  const color = getScoreColor(dim.score);
  return (
    <div className="flex flex-col rounded-xl cursor-default flex-shrink-0"
      style={{
        background: `linear-gradient(145deg, ${color}15, ${color}04)`,
        border: `1px solid ${color}35`,
        textAlign: align === 'left' ? 'left' : 'right',
        padding: '12px 14px',
        minWidth: '155px',
        boxShadow: `0 0 15px ${color}0a`,
      }}>
      <div className="flex items-center gap-1.5" style={{ justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
        <span style={{ fontSize: '34px', fontWeight: 900, color: color, lineHeight: 1, textShadow: `0 0 15px ${color}` }}>
          {dim.score}
        </span>
        <span style={{ fontSize: '16px', fontWeight: 700, color: `${color}88`, alignSelf: 'flex-end', paddingBottom: '3px' }}>分</span>
      </div>
      <span className="font-bold whitespace-nowrap" style={{ fontSize: '18px', color: 'white', marginTop: '6px', lineHeight: 1.2 }}>
        {dim.name}
      </span>
      <div className="mt-2.5 h-1.5 rounded-full overflow-hidden" style={{ background: `${color}18` }}>
        <div style={{ width: `${dim.score}%`, height: '100%', background: color, borderRadius: '999px', boxShadow: `0 0 6px ${color}` }} />
      </div>
    </div>
  );
}

export default function AIRadarPanel() {
  const evalData = useEval();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const DURATION = 1500; // 让仪表盘动画显得更久更酷炫一点
    const tick = () => {
      const t = Math.min((Date.now() - start) / DURATION, 1);
      // spring缓动弹性曲线
      setProgress(1 - Math.pow(1 - t, 4));
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, []);

  if (!evalData) return null;
  const { courseEvalData } = evalData;
  const dims = courseEvalData.aiScores.dimensions;
  const overall = courseEvalData.aiScores.overall;

  const leftDims = [dims[0], dims[1], dims[2]];
  const rightDims = [dims[3], dims[4], dims[5]];

  // 仪表盘常量设计
  const START_ANG = 145; 
  const END_ANG = 395;
  const TOTAL_ANG = END_ANG - START_ANG; // 250度扫角
  
  const pct = (overall / 100) * progress;
  const currentAngle = START_ANG + pct * TOTAL_ANG;
  
  const GAUGE_LENGTH = 2 * Math.PI * R_GAUGE * (TOTAL_ANG / 360);
  const activeDash = GAUGE_LENGTH * pct;

  // 光圈指针
  const pointerTail = polarToCartesian(CX, CY, R_GAUGE - 35, currentAngle);
  const pointerHead = polarToCartesian(CX, CY, R_GAUGE + 8, currentAngle);
  const pointerCenter = polarToCartesian(CX, CY, R_GAUGE, currentAngle);

  return (
    <div className="h-full flex flex-col" style={{ padding: '4px 8px 6px' }}>
      {/* 标题栏 */}
      <div className="flex items-center justify-between px-1 mb-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-1 h-4 rounded-full flex-shrink-0"
            style={{ background: 'linear-gradient(to bottom, #22d3ee, rgba(34,211,238,0.5))', boxShadow: '0 0 10px rgba(34,211,238,0.9)' }} />
          <span className="text-white font-bold font-semibold tracking-wide" style={{ fontSize: '24px' }}>AI综合评分</span>
        </div>
      </div>

      {/* 主体：三列布局 */}
      <div className="flex items-center flex-1 min-h-0 gap-2">
        {/* 左侧向内贴的分数列：弧形错位排布 */}
        <div className="flex flex-col justify-around h-full flex-shrink-0 py-2" style={{ width: '150px', zIndex: 10 }}>
          {leftDims.map((d, index) => {
            // 最残暴的内缩压阵模式：完全挤压边距空白
            const xOffset = index === 1 ? 65 : 125;
            return (
              <div key={d.id} style={{ transform: `translateX(${xOffset}px)` }}>
                <ScoreCard dim={d} align="left" />
              </div>
            );
          })}
        </div>

        {/* 核心仪表盘 (Gauge Chart) */}
        <div className="flex-1 flex items-center justify-center min-w-0 relative">
          <svg
            viewBox={`0 0 ${CX * 2} ${CY * 2.1}`}
            style={{ width: '100%', height: '100%', maxWidth: '390px', maxHeight: '390px', overflow: 'visible' }}
          >
            <defs>
              <linearGradient id="gaugeRainbow" x1="0%" y1="100%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ef4444" />       {/* 红 */}
                <stop offset="35%" stopColor="#f59e0b" />      {/* 黄橙 */}
                <stop offset="70%" stopColor="#10b981" />      {/* 绿 */}
                <stop offset="100%" stopColor="#06b6d4" />     {/* 青绿 */}
              </linearGradient>
              <filter id="gaugeGlow">
                <feGaussianBlur stdDeviation="3.5" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>

            {/* 背景暗轨 */}
            <path
              d={describeArc(CX, CY, R_GAUGE, START_ANG, END_ANG)}
              fill="none"
              stroke="rgba(255,255,255,0.05)"
              strokeWidth="16"
              strokeLinecap="round"
            />

            {/* 高亮彩虹轨 */}
            <path
              d={describeArc(CX, CY, R_GAUGE, START_ANG, END_ANG)}
              fill="none"
              stroke="url(#gaugeRainbow)"
              strokeWidth="16"
              strokeLinecap="round"
              strokeDasharray={`${activeDash} ${GAUGE_LENGTH}`}
              filter="url(#gaugeGlow)"
            />

            {/* 内侧精细多段刻度线 */}
            {Array.from({ length: 51 }).map((_, i) => {
              // 51 根刻度线, 每 5 度一根
              const a = START_ANG + i * (TOTAL_ANG / 50);
              const isMajor = i % 2 === 0;
              const innerR = isMajor ? R_GAUGE - 24 : R_GAUGE - 18;
              const pt1 = polarToCartesian(CX, CY, innerR, a);
              const pt2 = polarToCartesian(CX, CY, R_GAUGE - 12, a);
              const isActive = a <= currentAngle;
              
              return (
                <line key={i} x1={pt1.x} y1={pt1.y} x2={pt2.x} y2={pt2.y}
                  stroke={isActive ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.15)"}
                  strokeWidth={isMajor ? "2.5" : "1.2"} />
              );
            })}

            {/* 中心文本区 */}
            <text x={CX} y={CY + 5} textAnchor="middle" fill="#22d3ee"
              style={{ fontSize: '50px', fontWeight: 900, fontFamily: 'monospace', letterSpacing: '0.02em', textShadow: '0 0 20px rgba(34,211,238,0.7)' }}>
              {(overall * progress).toFixed(2)}
            </text>
            <text x={CX} y={CY + 45} textAnchor="middle" fill="white"
              style={{ fontSize: '24px', fontWeight: 'bold', letterSpacing: '0.05em' }}>
              综合评分
            </text>

            <rect x={CX - 45} y={CY + 60} width="90" height="4" fill="rgba(34,211,238,0.4)" rx="2" style={{ filter: 'drop-shadow(0 0 5px #22d3ee)' }} />

            {/* 高光极点指针 */}
            <line x1={pointerTail.x} y1={pointerTail.y} x2={pointerHead.x} y2={pointerHead.y}
              stroke="#fff" strokeWidth="2.5" strokeLinecap="round"
              style={{ filter: 'drop-shadow(0 0 6px #ffffff)' }} />
            <circle cx={pointerCenter.x} cy={pointerCenter.y} r="6" fill="#06b6d4" stroke="white" strokeWidth="2" style={{ filter: 'drop-shadow(0 0 6px #06b6d4)' }} />
            
          </svg>
        </div>

        {/* 右侧向内贴的分数列：弧形错位排布 */}
        <div className="flex flex-col justify-around h-full flex-shrink-0 py-2" style={{ width: '150px', zIndex: 10 }}>
          {rightDims.map((d, index) => {
            // 最残暴的内缩压阵模式：完全挤压边距空白
            const xOffset = index === 1 ? -65 : -125;
            return (
              <div key={d.id} style={{ transform: `translateX(${xOffset}px)` }}>
                <ScoreCard dim={d} align="right" />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
